import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const AnimatedBackground = ({ glowSize = 'normal', particlesOnly = false }) => {
  const canvasRef = useRef(null);
  const mousePosRef = useRef({ x: 0, y: 0 }); // Use ref instead of state to avoid re-renders
  const [isMobile, setIsMobile] = useState(false);
  
  // Glow settings based on prop
  const glowSettings = {
    normal: { size: 250, opacity: 0.18 },
    minimal: { size: 80, opacity: 0.04 },
  };
  const glow = glowSettings[glowSize] || glowSettings.normal;
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Full animated canvas with grid + particles
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resize();
    // Don't listen for resize on mobile - prevents layout jump when address bar shows/hides
    if (!isMobile) {
      window.addEventListener('resize', resize);
    }
    
    // Fewer particles for better performance
    const particles = [];
    const particleCount = isMobile ? 10 : 35;
    
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2.5 + 0.5,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.6 + 0.2,
        pulse: Math.random() * Math.PI * 2
      });
    }
    
    const animate = () => {
      ctx.fillStyle = particlesOnly ? 'rgba(10, 10, 10, 0.3)' : 'rgba(10, 10, 10, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      time += 0.008;
      
      // Animated wave grid lines (skip in particlesOnly mode and on mobile — heaviest loop)
      if (!particlesOnly && !isMobile) {
        const gridSpacing = isMobile ? 100 : 70;
        ctx.strokeStyle = 'rgba(255, 215, 0, 0.04)';
        ctx.lineWidth = 0.8;
        
        for (let x = 0; x < canvas.width + gridSpacing; x += gridSpacing) {
          ctx.beginPath();
          const offsetX = Math.sin(time + x * 0.01) * 8;
          ctx.moveTo(x + offsetX, 0);
          for (let y = 0; y < canvas.height; y += 30) {
            const waveX = Math.sin(time * 2 + y * 0.008 + x * 0.003) * 5;
            ctx.lineTo(x + offsetX + waveX, y);
          }
          ctx.stroke();
        }
        
        for (let y = 0; y < canvas.height + gridSpacing; y += gridSpacing) {
          ctx.beginPath();
          const offsetY = Math.cos(time + y * 0.01) * 8;
          ctx.moveTo(0, y + offsetY);
          for (let x = 0; x < canvas.width; x += 30) {
            const waveY = Math.cos(time * 2 + x * 0.008 + y * 0.003) * 5;
            ctx.lineTo(x, y + offsetY + waveY);
          }
          ctx.stroke();
        }
      }
      
      // Draw particles with glow
      particles.forEach((particle) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        particle.pulse += 0.03;
        
        // Wrap around
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        
        const pulseOpacity = particle.opacity * (0.6 + Math.sin(particle.pulse) * 0.4);
        
        if (isMobile) {
          // Cheap path for mobile: solid dot with alpha, no per-frame gradient creation
          ctx.fillStyle = `rgba(255, 215, 0, ${pulseOpacity})`;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size * 1.4, 0, Math.PI * 2);
          ctx.fill();
          return;
        }
        
        // Glow effect
        const gradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.size * 6
        );
        gradient.addColorStop(0, `rgba(255, 215, 0, ${pulseOpacity})`);
        gradient.addColorStop(0.4, `rgba(255, 215, 0, ${pulseOpacity * 0.3})`);
        gradient.addColorStop(1, 'rgba(255, 215, 0, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 6, 0, Math.PI * 2);
        ctx.fill();
        
        // Bright core
        ctx.fillStyle = `rgba(255, 215, 0, ${pulseOpacity * 2})`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 1.2, 0, Math.PI * 2);
        ctx.fill();
      });
      
      // Mouse glow (desktop) - configurable size
      if (!isMobile && mousePosRef.current.x && mousePosRef.current.y) {
        const gradient = ctx.createRadialGradient(
          mousePosRef.current.x, mousePosRef.current.y, 0,
          mousePosRef.current.x, mousePosRef.current.y, glow.size
        );
        gradient.addColorStop(0, `rgba(255, 215, 0, ${glow.opacity})`);
        gradient.addColorStop(0.5, `rgba(255, 215, 0, ${glow.opacity * 0.3})`);
        gradient.addColorStop(1, 'rgba(255, 215, 0, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(mousePosRef.current.x, mousePosRef.current.y, glow.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [isMobile]);

  // Mouse tracking - using ref to avoid re-renders
  useEffect(() => {
    if (isMobile) return;
    
    const handleMouseMove = (e) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  return (
    <>
      {/* Main animated canvas */}
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 0 }}
      />
      
      {/* Floating orbs - NO BLUR for performance (skipped on mobile) */}
      {!particlesOnly && !isMobile && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
          <motion.div
            className="absolute w-[700px] h-[700px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,215,0,0.05) 0%, transparent 50%)',
              top: '-15%',
              left: '-10%',
            }}
            animate={{
              x: [0, 80, 0],
              y: [0, 60, 0],
              scale: [1, 1.15, 1],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          
          <motion.div
            className="absolute w-[500px] h-[500px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,215,0,0.04) 0%, transparent 50%)',
              bottom: '-5%',
              right: '-5%',
            }}
            animate={{
              x: [0, -60, 0],
              y: [0, -50, 0],
              scale: [1.1, 1, 1.1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          
          <motion.div
            className="absolute w-[400px] h-[400px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(255,215,0,0.03) 0%, transparent 60%)',
              top: '40%',
              left: '50%',
            }}
            animate={{
              x: [-80, 80, -80],
              y: [-40, 40, -40],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>
      )}
      
      {/* Scan lines effect - hidden in particlesOnly mode */}
      {!particlesOnly && (
        <div
          className="fixed inset-0 pointer-events-none opacity-30"
          style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,215,0,0.02) 2px, rgba(255,215,0,0.02) 4px)',
            zIndex: 0,
          }}
        />
      )}
      
      {/* Moving accent lines - hidden in particlesOnly mode */}
      {!particlesOnly && (
        <>
          <motion.div
            className="fixed h-px w-[200%] pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.25), transparent)',
              top: '30%',
              left: '-50%',
              zIndex: 0,
            }}
            animate={{ x: ['-25%', '25%', '-25%'] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          
          <motion.div
            className="fixed h-px w-[200%] pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.2), transparent)',
              top: '70%',
              left: '-50%',
              zIndex: 0,
            }}
            animate={{ x: ['25%', '-25%', '25%'] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
          
          <motion.div
            className="fixed w-px h-[200%] pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, transparent, rgba(255,215,0,0.15), transparent)',
              left: '25%',
              top: '-50%',
              zIndex: 0,
            }}
            animate={{ y: ['-25%', '25%', '-25%'] }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />
          
          <motion.div
            className="fixed w-px h-[200%] pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, transparent, rgba(255,215,0,0.15), transparent)',
              left: '75%',
              top: '-50%',
              zIndex: 0,
            }}
            animate={{ y: ['25%', '-25%', '25%'] }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />
        </>
      )}
    </>
  );
};

export default AnimatedBackground;
